import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
  useTheme,
  Divider,
} from "@mui/material";
import {
  ExpandMore,
  Person,
  Logout,
  LightMode,
  DarkMode,
  SettingsBrightness,
  Menu as MenuIcon,
  Settings,
  Groups,
  Lock,
  CreditCard,
  ChevronRight,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useThemeMode } from "../../../context/ThemeContext";

interface User {
  id: number;
  email?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  isSuperuser?: boolean;
  hasUsablePassword?: string | boolean;
}

interface Team {
  id: number;
  name?: string;
  slug?: string;
}

interface TopNavProps {
  user: User | null;
  team?: Team | null;
  title: string;
  onMenuToggle?: () => void;
}

/**
 * TopNav - React equivalent of templates/web/components/top_nav.html
 *
 * Provides the blue top navigation bar with:
 * - Project/app title on the left
 * - User menu with profile, logout
 * - Dark mode toggle
 */
const TopNav: React.FC<TopNavProps> = ({ user, team, title, onMenuToggle }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { mode, setThemeMode } = useThemeMode();
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [themeMenuAnchor, setThemeMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [subscriptionMenuAnchor, setSubscriptionMenuAnchor] =
    useState<null | HTMLElement>(null);

  // Get the appropriate icon for the current mode
  const getThemeIcon = () => {
    if (mode === "light") return <LightMode fontSize="small" />;
    if (mode === "dark") return <DarkMode fontSize="small" />;
    return <SettingsBrightness fontSize="small" />; // auto mode
  };

  // Get tooltip text for theme button
  const getThemeLabel = () => {
    if (mode === "light") return "Light";
    if (mode === "dark") return "Dark";
    return "Auto";
  };

  const handleThemeMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setThemeMenuAnchor(event.currentTarget);
  };

  const handleThemeMenuClose = () => {
    setThemeMenuAnchor(null);
  };

  const handleThemeSelect = (selectedMode: "light" | "dark" | "auto") => {
    setThemeMode(selectedMode);
    handleThemeMenuClose();
  };

  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
    setSubscriptionMenuAnchor(null); // Close subscription submenu when main menu closes
  };

  const handleSubscriptionMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setSubscriptionMenuAnchor(event.currentTarget);
  };

  const handleSubscriptionMenuClose = () => {
    setSubscriptionMenuAnchor(null);
  };

  const getUserDisplayName = () => {
    if (!user) return "Guest";
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.username || user.email || "User";
  };

  const handleProfileClick = () => {
    handleUserMenuClose();
    navigate("/profile");
  };

  const handleTeamSettingsClick = () => {
    handleUserMenuClose();
    if (team) {
      navigate(`/teams/settings/${team.slug}`);
    }
  };

  const handleSwitchTeamClick = () => {
    handleUserMenuClose();
    navigate("/teams/");
  };

  const handleChangePasswordClick = () => {
    handleUserMenuClose();
    navigate("/change-password");
  };

  const handleSubscriptionDemoClick = () => {
    handleUserMenuClose();
    if (team) {
      navigate(`/a/${team.slug}/subscription/demo`);
    }
  };

  const handleMeteredBillingClick = () => {
    handleUserMenuClose();
    if (team) {
      navigate(`/a/${team.slug}/subscription/metered-billing-demo`);
    }
  };

  const handleLogoutClick = () => {
    handleUserMenuClose();
    navigate("/auth/logout");
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        width: "100%",
        // No bgcolor here on purpose: the theme's MuiAppBar override pairs the
        // surface colour with a matching text colour for each mode. Setting a
        // background inline overrides the background but not the text colour,
        // which is how this bar ended up white-on-white.
        zIndex: theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ minHeight: "56px !important", py: 0.5 }}>
        {/* Mobile Menu Button - Only show when onMenuToggle is provided (mobile) */}
        {onMenuToggle && (
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={onMenuToggle}
            sx={{ mr: { xs: 1, sm: 2 }, minWidth: 44, minHeight: 44 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Left: Project Title */}
        <Typography
          variant="h6"
          noWrap
          component="div"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            fontSize: "1.1rem",
            fontWeight: 500,
            cursor: "pointer",
          }}
          onClick={() => navigate("/")}
        >
          {title}
        </Typography>

        {/* Right: Navigation Items */}
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}
        >
          {user && (
            <>
              {/* User Menu */}
              <Button
                color="inherit"
                aria-label={getUserDisplayName()}
                onClick={handleUserMenuClick}
                endIcon={<ExpandMore />}
                startIcon={
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: "0.75rem",
                      bgcolor: "surfaces.surfaceRaised",
                      color: "text.primary",
                    }}
                  >
                    {getUserDisplayName().charAt(0).toUpperCase()}
                  </Avatar>
                }
                sx={{
                  textTransform: "none",
                  fontSize: "0.875rem",
                  minHeight: 44,
                  "& .MuiButton-startIcon": { mr: { xs: 0, sm: 1 } },
                  "&:hover": {
                    bgcolor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{ display: { xs: "none", sm: "inline" } }}
                >
                  {getUserDisplayName()}
                </Box>
              </Button>

              <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={handleUserMenuClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                PaperProps={{
                  sx: { minWidth: 200 },
                }}
              >
                {/* Team Actions - Only show if user has a team */}
                {team && [
                  <MenuItem
                    key="team-settings"
                    onClick={handleTeamSettingsClick}
                  >
                    <Settings sx={{ mr: 2 }} />
                    Team Settings
                  </MenuItem>,
                  <MenuItem key="switch-team" onClick={handleSwitchTeamClick}>
                    <Groups sx={{ mr: 2 }} />
                    Switch Team
                  </MenuItem>,
                  <Divider key="team-divider" />,
                ]}

                {/* Subscription Actions - Only show if user has a team */}
                {team && [
                  <MenuItem
                    key="subscription"
                    onClick={handleSubscriptionMenuOpen}
                    onMouseEnter={onMenuToggle ? undefined : handleSubscriptionMenuOpen}
                  >
                    <CreditCard sx={{ mr: 2 }} />
                    Subscription
                    <ChevronRight sx={{ ml: "auto" }} />
                  </MenuItem>,
                  <Divider key="subscription-divider" />,
                ]}

                {/* User Actions */}
                <MenuItem onClick={handleProfileClick}>
                  <Person sx={{ mr: 2 }} />
                  Profile
                </MenuItem>

                {user.hasUsablePassword && (
                  <MenuItem onClick={handleChangePasswordClick}>
                    <Lock sx={{ mr: 2 }} />
                    Change Password
                  </MenuItem>
                )}

                <Divider />
                <MenuItem onClick={handleLogoutClick}>
                  <Logout sx={{ mr: 2 }} />
                  Logout
                </MenuItem>
              </Menu>

              {/* Nested Subscription Submenu */}
              {team && (
                <Menu
                  anchorEl={subscriptionMenuAnchor}
                  open={Boolean(subscriptionMenuAnchor)}
                  onClose={handleSubscriptionMenuClose}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  PaperProps={{
                    sx: { minWidth: 200 },
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      navigate(`/a/${team.slug}/subscription`);
                      handleUserMenuClose();
                    }}
                  >
                    <CreditCard sx={{ mr: 2 }} />
                    Subscription Overview
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleSubscriptionDemoClick}>
                    Subscription Demo
                  </MenuItem>
                  <MenuItem onClick={handleMeteredBillingClick}>
                    Metered Billing
                  </MenuItem>
                </Menu>
              )}
            </>
          )}

          {/* Login/Signup for non-authenticated users */}
          {!user && (
            <>
              <Button
                color="inherit"
                variant="outlined"
                onClick={() => navigate("/auth/signup")}
                sx={{
                  textTransform: "none",
                  borderColor: "rgba(255, 255, 255, 0.5)",
                  "&:hover": {
                    borderColor: "white",
                    bgcolor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                Sign Up
              </Button>
              <Button
                color="inherit"
                onClick={() => navigate("/auth/login")}
                sx={{
                  textTransform: "none",
                  "&:hover": {
                    bgcolor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                Sign In
              </Button>
            </>
          )}

          {/* Theme Selector Dropdown */}
          <Button
            color="inherit"
            size="small"
            onClick={handleThemeMenuClick}
            aria-label={`Theme: ${getThemeLabel()}`}
            startIcon={getThemeIcon()}
            sx={{
              textTransform: "none",
              minWidth: "auto",
              px: { xs: 1, sm: 1.5 },
              "& .MuiButton-startIcon": { m: { xs: 0, sm: undefined } },
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>{getThemeLabel()}</Box>
          </Button>

          <Menu
            anchorEl={themeMenuAnchor}
            open={Boolean(themeMenuAnchor)}
            onClose={handleThemeMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            PaperProps={{
              sx: { minWidth: 140 },
            }}
          >
            <MenuItem
              onClick={() => handleThemeSelect("light")}
              selected={mode === "light"}
            >
              <LightMode sx={{ mr: 2, fontSize: 20 }} />
              Light
            </MenuItem>
            <MenuItem
              onClick={() => handleThemeSelect("dark")}
              selected={mode === "dark"}
            >
              <DarkMode sx={{ mr: 2, fontSize: 20 }} />
              Dark
            </MenuItem>
            <MenuItem
              onClick={() => handleThemeSelect("auto")}
              selected={mode === "auto"}
            >
              <SettingsBrightness sx={{ mr: 2, fontSize: 20 }} />
              Auto
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopNav;
